import { supabase } from '../lib/supabase';

const commToSnakeCase = (obj) => {
  return {
    client_id: obj?.clientId,
    project_id: obj?.projectId,
    user_id: obj?.userId,
    communication_type: obj?.communicationType,
    subject: obj?.subject,
    message: obj?.message,
    duration_minutes: obj?.durationMinutes,
    attachments: obj?.attachments,
    metadata: obj?.metadata,
  };
};

const createCommunication = async (commData) => {
  const { data, error } = await supabase?.from('communications')?.insert([commToSnakeCase(commData)])?.select()?.single();

  if (error) throw error;
  return data;
};

const updateCommunication = async (id, commData) => {
  const { data, error } = await supabase?.from('communications')?.update(commToSnakeCase(commData))?.eq('id', id)?.select()?.single();

  if (error) throw error;
  return data;
};

const deleteCommunication = async (id) => {
  const { error } = await supabase?.from('communications')?.delete()?.eq('id', id);

  if (error) throw error;
};

// Get all communications for a client
const getClientCommunications = async (clientId, filters = {}) => {
  try {
    let query = supabase
      ?.from('client_communications')
      ?.select(`
        *,
        client:leads(id, name, email, phone),
        project:projects(id, project_number, title),
        user:user_profiles(id, full_name, email)
      `)
      ?.eq('client_id', clientId)
      ?.order('created_at', { ascending: false });

    if (filters?.projectId) {
      query = query?.eq('project_id', filters?.projectId);
    }

    if (filters?.communicationType) {
      query = query?.eq('communication_type', filters?.communicationType);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(comm => ({
      id: comm?.id,
      clientId: comm?.client_id,
      projectId: comm?.project_id,
      userId: comm?.user_id,
      communicationType: comm?.communication_type,
      subject: comm?.subject,
      message: comm?.message,
      durationMinutes: comm?.duration_minutes,
      attachments: comm?.attachments || [],
      metadata: comm?.metadata,
      createdAt: comm?.created_at,
      client: comm?.client,
      project: comm?.project,
      user: comm?.user,
    })) || [];
  } catch (error) {
    console.error('Error fetching client communications:', error);
    throw error;
  }
};

// Get all communications (admin view)
const getAllCommunications = async (filters = {}) => {
  try {
    let query = supabase
      ?.from('client_communications')
      ?.select(`
        *,
        client:leads(id, name, email, phone),
        project:projects(id, project_number, title),
        user:user_profiles(id, full_name, email)
      `)
      ?.order('created_at', { ascending: false })
      ?.limit(filters?.limit || 100);

    if (filters?.communicationType) {
      query = query?.eq('communication_type', filters?.communicationType);
    }

    if (filters?.search) {
      query = query?.or(`subject.ilike.%${filters?.search}%,message.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(comm => ({
      id: comm?.id,
      clientId: comm?.client_id,
      projectId: comm?.project_id,
      userId: comm?.user_id,
      communicationType: comm?.communication_type,
      subject: comm?.subject,
      message: comm?.message,
      durationMinutes: comm?.duration_minutes,
      attachments: comm?.attachments || [],
      metadata: comm?.metadata,
      createdAt: comm?.created_at,
      client: comm?.client,
      project: comm?.project,
      user: comm?.user,
    })) || [];
  } catch (error) {
    console.error('Error fetching all communications:', error);
    throw error;
  }
};

// Get communication statistics
const getCommunicationStats = async (days = 7) => {
  try {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - days);

    const { data, error } = await supabase
      ?.from('client_communications')
      ?.select('communication_type, created_at')
      ?.gte('created_at', startDate?.toISOString());

    if (error) throw error;

    const stats = {
      total: data?.length || 0,
      byType: {},
      byDay: {},
    };

    // Group by type
    data?.forEach(comm => {
      const type = comm?.communication_type;
      if (!stats?.byType?.[type]) {
        stats.byType[type] = 0;
      }
      stats.byType[type]++;

      // Group by day
      const day = new Date(comm?.created_at)?.toLocaleDateString();
      if (!stats?.byDay?.[day]) {
        stats.byDay[day] = 0;
      }
      stats.byDay[day]++;
    });

    return stats;
  } catch (error) {
    console.error('Error fetching communication stats:', error);
    throw error;
  }
};

// Get recent conversations (grouped by client)
const getRecentConversations = async (limit = 20) => {
  try {
    const { data, error } = await supabase
      ?.from('client_communications')
      ?.select(`
        *,
        client:leads(id, name, email, phone, avatar),
        project:projects(id, project_number, title)
      `)
      ?.order('created_at', { ascending: false })
      ?.limit(limit);

    if (error) throw error;

    // Group by client
    const conversations = {};
    data?.forEach(comm => {
      const clientId = comm?.client_id;
      if (!conversations?.[clientId]) {
        conversations[clientId] = {
          client: comm?.client,
          lastMessage: comm,
          messageCount: 0,
          unreadCount: 0,
        };
      }
      conversations[clientId].messageCount++;
    });

    return Object?.values(conversations);
  } catch (error) {
    console.error('Error fetching recent conversations:', error);
    throw error;
  }
};

// Search communications
const searchCommunications = async (searchTerm) => {
  try {
    const { data, error } = await supabase
      ?.from('client_communications')
      ?.select(`
        *,
        client:leads(id, name, email, phone),
        project:projects(id, project_number, title)
      `)
      ?.or(`subject.ilike.%${searchTerm}%,message.ilike.%${searchTerm}%`)
      ?.order('created_at', { ascending: false })
      ?.limit(50);

    if (error) throw error;

    return data?.map(comm => ({
      id: comm?.id,
      clientId: comm?.client_id,
      projectId: comm?.project_id,
      communicationType: comm?.communication_type,
      subject: comm?.subject,
      message: comm?.message,
      attachments: comm?.attachments || [],
      createdAt: comm?.created_at,
      client: comm?.client,
      project: comm?.project,
    })) || [];
  } catch (error) {
    console.error('Error searching communications:', error);
    throw error;
  }
};

export const communicationService = {
  getRecentConversations,
  getClientCommunications,
  createCommunication,
  updateCommunication,
  deleteCommunication,
  getCommunicationStats,
  searchCommunications,
};
